/**
 * GroupedBarChart Component
 * Side-by-side bar chart for comparing assets across KPIs
 */

import Plot from 'react-plotly.js'
import { Data, Layout } from 'plotly.js'

interface BarDataSeries {
  name: string
  values: number[]
  color?: string
}

interface GroupedBarChartProps {
  data: BarDataSeries[]
  categories: string[]
  title?: string
  xAxisTitle?: string
  yAxisTitle?: string
  height?: number
  showLegend?: boolean
  horizontal?: boolean
  showValues?: boolean
  className?: string
}

// Default color palette
const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
]

export default function GroupedBarChart({
  data,
  categories,
  title,
  xAxisTitle,
  yAxisTitle,
  height = 300,
  showLegend = true,
  horizontal = false,
  showValues = false,
  className
}: GroupedBarChartProps) {
  const traces: Data[] = data.map((series, index) => {
    const trace: Data = {
      type: 'bar',
      name: series.name,
      x: horizontal ? series.values : categories,
      y: horizontal ? categories : series.values,
      orientation: horizontal ? 'h' : 'v',
      marker: {
        color: series.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
        line: {
          color: (series.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]),
          width: 1
        }
      },
      hovertemplate: horizontal
        ? `<b>%{y}</b><br>${series.name}: %{x:.1f}<extra></extra>`
        : `<b>%{x}</b><br>${series.name}: %{y:.1f}<extra></extra>`,
      textposition: 'outside',
      textfont: { size: 10, color: '#94a3b8' }
    }

    if (showValues) {
      trace.text = series.values.map(v => v.toFixed(1))
    }

    return trace
  })

  const layout: Partial<Layout> = {
    title: title ? {
      text: title,
      font: { color: '#e2e8f0', size: 14 }
    } : undefined,
    height,
    margin: {
      t: title ? 50 : 30,
      r: 20,
      b: horizontal ? 40 : 60,
      l: horizontal ? 100 : 50
    },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', family: 'Inter, sans-serif' },
    barmode: 'group',
    bargap: 0.15,
    bargroupgap: 0.1,
    xaxis: {
      title: xAxisTitle ? { text: xAxisTitle, font: { color: '#94a3b8', size: 12 } } : undefined,
      gridcolor: horizontal ? '#334155' : 'transparent',
      linecolor: '#475569',
      tickfont: { size: 10 },
      showgrid: horizontal,
      zeroline: false,
      tickangle: horizontal ? 0 : -45
    },
    yaxis: {
      title: yAxisTitle ? { text: yAxisTitle, font: { color: '#94a3b8', size: 12 } } : undefined,
      gridcolor: horizontal ? 'transparent' : '#334155',
      linecolor: '#475569',
      tickfont: { size: 10 },
      showgrid: !horizontal,
      zeroline: false
    },
    showlegend: showLegend,
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: 1.02,
      xanchor: 'right',
      x: 1,
      font: { size: 10 },
      bgcolor: 'transparent'
    },
    hovermode: 'closest'
  }

  const config = {
    displayModeBar: false,
    responsive: true
  }

  return (
    <div className={className}>
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
      />
    </div>
  )
}
